import { Anchor, Box, Breadcrumbs, Button, Group, LoadingOverlay, SegmentedControl, Text } from "@mantine/core"
import { MonthPickerInput } from "@mantine/dates"
import { useDebouncedValue } from "@mantine/hooks"
import { CalendarIcon, ShoppingCartIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import "dayjs/locale/mn"
import "dayjs/locale/ko"
import "dayjs/locale/en"
import "dayjs/locale/zh"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import useSwr from "swr"
import { DashboardApi } from "../../apis"
import { usePermission } from "../../components/permission"
import { DeniedCard } from "../../components/ui"
import { ReturnsDashboardWidget } from "../../components/return/dashboard"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { AnalyticsControls, SalesAnalytics } from "./analytics"
import { DashboardReal } from "./real"
import { useIsBuyer } from "./use-portal"
import classes from "./real.module.css"

type TabValue = "overview" | "analytics"

export function Dashboard() {
  const { t, i18n } = useTranslation(["dashboard", "common"])
  const dayjsLocale = i18n.language === "zh" ? "zh-cn" : i18n.language
  const today = dayjs()
  const startOfMonth = dayjs().startOf("month")
  const endOfMonth = dayjs().endOf("month")
  const [pieFilter, setPieFilter] = useState<string | null>("WEEK")
  const [debouncedFilter] = useDebouncedValue(pieFilter, 600)
  const [date, setDate] = useState<string | null>(today.format("YYYY-MM-DD"))
  const [startDate, setStartDate] = useState<string | null>(startOfMonth.format("YYYY-MM-DD"))
  const [debouncedStartDate] = useDebouncedValue(startDate, 600)
  const [endDate, setEndDate] = useState<string | null>(endOfMonth.format("YYYY-MM-DD"))
  const [debouncedEndDate] = useDebouncedValue(endDate, 600)

  const isBuyer = useIsBuyer()
  const navigate = useNavigate()
  // Sales analytics is supplier-only. It scopes orders with sender OR receiver,
  // so a buyer's purchases DO land in it, but cost comes from BusinessVariant
  // rows scoped to the caller's business — which a buyer does not own. Cost
  // then resolves to 0 and the buyer is told their own spend ran at 100%
  // margin. Until there is a buyer-shaped analytics view, hide the tab.
  const canSeeAnalytics = !isBuyer

  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get("tab") === "analytics" ? "analytics" : "overview"
  const activeTab: TabValue = requestedTab === "analytics" && canSeeAnalytics ? "analytics" : "overview"
  const handleTabChange = (value: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (!value || value === "overview") next.delete("tab")
    else next.set("tab", value)
    setSearchParams(next, { replace: true })
  }

  const ORD_DASH = usePermission("ORD_DASH")
  // BE routes/api/return-analytics/dashboard.ts requires RETURN_ANALYTICS_VIEW.
  const RETURN_ANALYTICS = usePermission("RETURN_ANALYTICS_VIEW")

  // The month picker drives every card; keep the chart range on the same month.
  const handleMonthChange = (val: Date | string | null) => {
    if (!val) {
      setDate(null)
      return
    }
    const m = dayjs(val)
    setDate(m.format("YYYY-MM-DD"))
    setStartDate(m.startOf("month").format("YYYY-MM-DD"))
    setEndDate(m.endOf("month").format("YYYY-MM-DD"))
  }

  const { data: chartData, isLoading } = useSwr(
    ORD_DASH.hasAccess && activeTab === "overview"
      ? `/erp/order/category/${debouncedFilter}/${date}/${debouncedStartDate}/${debouncedEndDate}`
      : null,
    async () => {
      try {
        if (!date || !startDate || !endDate || !pieFilter) return
        let res = await DashboardApi.get({
          date,
          startDate: debouncedStartDate,
          endDate: debouncedEndDate,
          pieFilter: debouncedFilter,
        })
        return res
      } catch (err) {
        message.error((err as HttpHandler)?.message!)
      }
    },
  )

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box>
          {!isBuyer && (
            <Breadcrumbs separator="/" className={classes.crumbs}>
              <Anchor component={Link} to="/" inherit>{t("home")}</Anchor>
              <Text inherit>{t("breadcrumb")}</Text>
            </Breadcrumbs>
          )}
          <Text component="h1" className={classes.pageTitle} m={0}>{t("breadcrumb")}</Text>
        </Box>
        <Group gap="sm">
          {isBuyer && (
            <Button leftSection={<ShoppingCartIcon size={16} />} onClick={() => navigate("/buyer-sales/new")}>
              {t("buyerActions.orderFromCatalog")}
            </Button>
          )}
          {canSeeAnalytics && (
            <SegmentedControl
              value={activeTab}
              onChange={handleTabChange}
              data={[
                { value: "overview", label: t("tabs.overview") },
                { value: "analytics", label: t("tabs.analytics") },
              ]}
            />
          )}
          {activeTab === "analytics" && ORD_DASH.hasAccess && <AnalyticsControls />}
          {activeTab === "overview" && (
            <MonthPickerInput
              value={date ? dayjs(date).toDate() : null}
              onChange={handleMonthChange}
              valueFormat={t("dateFormat")}
              locale={dayjsLocale}
              leftSection={<CalendarIcon size={16} />}
              w={200}
              aria-label={t("selectedMonth")}
            />
          )}
        </Group>
      </Box>

      {activeTab === "overview" ? (
        <Box pos="relative">
          <LoadingOverlay visible={isLoading} />
          {ORD_DASH.hasAccess ? (
            <DashboardReal
              data={chartData}
              date={date}
              filter={pieFilter}
              setFilter={setPieFilter}
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
            />
          ) : (
            ORD_DASH.accessDenied()
          )}

          {/* §D9 — Returns contribution to the main Overview. Supplier-side:
              it reads the supplier returns queue, ranks the buyers who return
              most, and links into /returns/list, none of which is the buyer's
              own returns picture. */}
          {ORD_DASH.hasAccess && !isBuyer ? (
            <Box className={classes.returnsRow}>
              {RETURN_ANALYTICS.hasAccess ? (
                <ReturnsDashboardWidget />
              ) : (
                <DeniedCard permission="RETURN_ANALYTICS_VIEW" title={t("returnsWidget.title")} />
              )}
            </Box>
          ) : null}
        </Box>
      ) : (
        ORD_DASH.hasAccess ? <SalesAnalytics /> : ORD_DASH.accessDenied()
      )}
    </Box>
  )
}
